import React, { useMemo, useState } from 'react';
import { NavBar } from '../components/NavBar';
import { StatCard } from '../components/StatCard';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { ScheduleReportsModal } from '../components/ScheduleReportsModal';
import { SearchBar } from '../components/SearchBar';
import { FilterChip } from '../components/FilterChip';
import { DateRangePicker } from '../components/DateRangePicker';
import { Plus, Download, MoreVertical, Calendar, FileText, Edit, Pause, Play, Trash2, RefreshCw, AlertCircle, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
// Mock data for scheduled reports
const mockScheduledReports = [{
  id: '1',
  name: 'Monthly Subscription Report',
  frequency: 'Monthly',
  nextRun: 'Jan 15, 2026 at 9:00 AM',
  recipients: ['chris@winery.com', 'sarah@winery.com'],
  status: 'Active' as const
}, {
  id: '2',
  name: 'Weekly Revenue Summary',
  frequency: 'Weekly',
  nextRun: 'Jan 10, 2026 at 8:00 AM',
  recipients: ['finance@winery.com'],
  status: 'Active' as const
}, {
  id: '3',
  name: 'Quarterly Churn Analysis',
  frequency: 'Quarterly',
  nextRun: 'Mar 1, 2026 at 10:00 AM',
  recipients: ['team@winery.com', 'analytics@winery.com', 'ops@winery.com'],
  status: 'Paused' as const
}];
// Mock data for report history
const mockReportHistory = [{
  id: '1',
  name: 'Monthly Subscription Report',
  generated: 'Jan 12, 2026 at 2:30 PM',
  generatedBy: 'Scheduled',
  fileSize: '2.4 MB',
  status: 'Completed' as const
}, {
  id: '2',
  name: 'Weekly Revenue Summary',
  generated: 'Jan 10, 2026 at 8:00 AM',
  generatedBy: 'Scheduled',
  fileSize: '1.8 MB',
  status: 'Completed' as const
}, {
  id: '3',
  name: 'Custom Member Export',
  generated: 'Jan 8, 2026 at 3:45 PM',
  generatedBy: 'Chris Admin',
  fileSize: '5.1 MB',
  status: 'Completed' as const
}, {
  id: '4',
  name: 'Shipment Report',
  generated: 'Jan 7, 2026 at 11:20 AM',
  generatedBy: 'Sarah Admin',
  fileSize: '3.2 MB',
  status: 'Failed' as const,
  error: 'Database connection timeout'
}, {
  id: '5',
  name: 'Revenue Report',
  generated: 'Jan 6, 2026 at 9:15 AM',
  generatedBy: 'Scheduled',
  fileSize: '—',
  status: 'Processing' as const
}];
export function ReportsPage() {
  const [activeTab, setActiveTab] = useState<'scheduled' | 'history'>('scheduled');
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reportTypeFilter, setReportTypeFilter] = useState<string[]>([]);
  const [dateRangeFilter, setDateRangeFilter] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: '',
    endDate: ''
  });
  // Filter report history
  const filteredReportHistory = useMemo(() => {
    return mockReportHistory.filter(report => {
      // Search filter
      if (searchQuery && !report.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Report type filter
      if (reportTypeFilter.length > 0) {
        const matchesType = reportTypeFilter.some(type => {
          if (type === 'all') return true;
          return report.name.toLowerCase().includes(type.toLowerCase());
        });
        if (!matchesType) return false;
      }
      return true;
    });
  }, [searchQuery, reportTypeFilter]);
  const handleScheduleReport = (schedule: any) => {
    console.log('Schedule report:', schedule);
    setIsScheduleModalOpen(false);
  };
  const handleDownload = (reportId: string) => {
    console.log('Download report:', reportId);
  };
  const handleRetry = (reportId: string) => {
    console.log('Retry report:', reportId);
  };
  const handleEditSchedule = (reportId: string) => {
    console.log('Edit schedule:', reportId);
    setIsScheduleModalOpen(true);
  };
  const handleTogglePause = (reportId: string) => {
    console.log('Toggle pause:', reportId);
  };
  const handleDeleteSchedule = (reportId: string) => {
    console.log('Delete schedule:', reportId);
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-8771">
      {/* NavBar */}
      <div className="mb-6" data-id="element-8772">
        <NavBar tabs={[]} title="Reports" subtitle="Schedule, generate, and download reports" data-id="element-8773" />
      </div>

      {/* Header with Button */}
      <div className="flex items-center justify-between mb-8" data-id="element-8774">
        <div data-id="element-8775"></div>
        <Button variant="primary" size="md" onClick={() => setIsScheduleModalOpen(true)} iconLeft={<Plus className="w-4 h-4" data-id="element-8777" />} data-id="element-8776">
          Schedule New Report
        </Button>
      </div>

      {/* Stat Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-8778">
        <StatCard label="Reports Generated" value="24" secondaryValue="This month" data-id="element-8779" />
        <StatCard label="Scheduled Reports" value="6" secondaryValue="Active" data-id="element-8780" />
        <StatCard label="Storage Used" value="1.2 GB" secondaryValue="of 5 GB" data-id="element-8781">
          <div className="w-full bg-[#E2E8F0] rounded-full h-2 mt-2" data-id="element-8782">
            <div className="bg-[#0EA5E9] h-2 rounded-full" style={{
            width: '24%'
          }} data-id="element-8783" />
          </div>
        </StatCard>
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-8784" />

      {/* Tab Navigation */}
      <div className="border-b border-[#E2E8F0] mb-6" data-id="element-8785">
        <div className="flex gap-0" data-id="element-8786">
          <button onClick={() => setActiveTab('scheduled')} className={`h-[44px] px-4 text-[14px] font-medium transition-colors duration-200 ${activeTab === 'scheduled' ? 'text-[#0EA5E9] border-b-2 border-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-8787">
            Scheduled Reports
          </button>
          <button onClick={() => setActiveTab('history')} className={`h-[44px] px-4 text-[14px] font-medium transition-colors duration-200 ${activeTab === 'history' ? 'text-[#0EA5E9] border-b-2 border-[#0EA5E9]' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-8788">
            Report History
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'scheduled' /* Scheduled Reports Tab */ ? <div data-id="element-8789">
          {mockScheduledReports.length > 0 ? <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8790">
              <div className="overflow-x-auto" data-id="element-8791">
                <table className="w-full" data-id="element-8792">
                  <thead className="bg-[#F1F5F9]" data-id="element-8793">
                    <tr data-id="element-8794">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8795">
                        Report Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8796">
                        Frequency
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8797">
                        Next Run
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8798">
                        Recipients
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8799">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8800">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8801">
                    {mockScheduledReports.map(report => <tr key={report.id} className="hover:bg-[#F8FAFC] transition-colors" style={{
                height: '52px'
              }} data-id="element-8802">
                        <td className="px-4 py-3" data-id="element-8803">
                          <button onClick={() => handleEditSchedule(report.id)} className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-8804">
                            {report.name}
                          </button>
                        </td>
                        <td className="px-4 py-3" data-id="element-8805">
                          <span className="text-[14px] text-[#475569]" data-id="element-8806">
                            {report.frequency}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-8807">
                          <span className="text-[14px] text-[#475569]" data-id="element-8808">
                            {report.nextRun}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-8809">
                          {report.recipients.length === 1 ? <span className="text-[14px] text-[#475569]" data-id="element-8810">
                              {report.recipients[0]}
                            </span> : <span className="text-[14px] text-[#475569] cursor-help" title={report.recipients.join(', ')} data-id="element-8811">
                              {report.recipients.length} recipients
                            </span>}
                        </td>
                        <td className="px-4 py-3" data-id="element-8812">
                          <Badge variant={report.status === 'Active' ? 'success' : 'default'} data-id="element-8813">
                            {report.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3" data-id="element-8814">
                          <div className="flex items-center gap-2" data-id="element-8815">
                            <button onClick={() => handleEditSchedule(report.id)} className="p-1.5 text-[#64748B] hover:text-[#0EA5E9] hover:bg-[#F1F5F9] rounded transition-colors" title="Edit" data-id="element-8816">
                              <Edit className="w-4 h-4" data-id="element-8817" />
                            </button>
                            <button onClick={() => handleTogglePause(report.id)} className="p-1.5 text-[#64748B] hover:text-[#0EA5E9] hover:bg-[#F1F5F9] rounded transition-colors" title={report.status === 'Active' ? 'Pause' : 'Resume'} data-id="element-8818">
                              {report.status === 'Active' ? <Pause className="w-4 h-4" data-id="element-8819" /> : <Play className="w-4 h-4" data-id="element-8820" />}
                            </button>
                            <button onClick={() => handleDeleteSchedule(report.id)} className="p-1.5 text-[#64748B] hover:text-[#EF4444] hover:bg-[#FEF2F2] rounded transition-colors" title="Delete" data-id="element-8821">
                              <Trash2 className="w-4 h-4" data-id="element-8822" />
                            </button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div> /* Empty State for Scheduled Reports */ : <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8823">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8824">
                <Calendar className="w-8 h-8 text-[#94A3B8]" data-id="element-8825" />
              </div>
              <h3 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-8826">
                No scheduled reports yet
              </h3>
              <p className="text-[14px] text-[#64748B] mb-6 max-w-md mx-auto" data-id="element-8827">
                Set up automated reports to be delivered to your inbox
              </p>
              <Button variant="primary" onClick={() => setIsScheduleModalOpen(true)} data-id="element-8828">
                Schedule Your First Report
              </Button>
            </div>}
        </div> /* Report History Tab */ : <div data-id="element-8829">
          {/* Filter Bar */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8830">
            <div className="flex flex-col md:flex-row gap-4" data-id="element-8831">
              <div className="flex-1" data-id="element-8832">
                <SearchBar placeholder="Search reports..." value={searchQuery} onChange={setSearchQuery} data-id="element-8833" />
              </div>
              <div className="flex gap-3" data-id="element-8834">
                <FilterChip filter={{
              id: 'reportType',
              label: 'Report Type',
              options: [{
                id: 'all',
                label: 'All Types',
                value: 'all'
              }, {
                id: 'subscription',
                label: 'Subscription Report',
                value: 'subscription'
              }, {
                id: 'revenue',
                label: 'Revenue Report',
                value: 'revenue'
              }, {
                id: 'churn',
                label: 'Churn Report',
                value: 'churn'
              }, {
                id: 'member',
                label: 'Member Report',
                value: 'member'
              }, {
                id: 'shipment',
                label: 'Shipment Report',
                value: 'shipment'
              }],
              selectedValue: reportTypeFilter[0] || 'all'
            }} onSelect={value => setReportTypeFilter([value])} data-id="element-8835" />
              </div>
            </div>
          </div>

          {filteredReportHistory.length > 0 ? <>
              <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8836">
                <div className="overflow-x-auto" data-id="element-8837">
                  <table className="w-full" data-id="element-8838">
                    <thead className="bg-[#F1F5F9]" data-id="element-8839">
                      <tr data-id="element-8840">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8841">
                          Report Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8842">
                          Generated
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8843">
                          Generated By
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8844">
                          File Size
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8845">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-8846">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-8847">
                      {filteredReportHistory.map(report => <tr key={report.id} className="hover:bg-[#F8FAFC] transition-colors" style={{
                  height: '52px'
                }} data-id="element-8848">
                          <td className="px-4 py-3" data-id="element-8849">
                            <span className="text-[14px] text-[#0F172A]" data-id="element-8850">
                              {report.name}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-8851">
                            <span className="text-[14px] text-[#475569]" data-id="element-8852">
                              {report.generated}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-8853">
                            <div className="flex items-center gap-2" data-id="element-8854">
                              {report.generatedBy === 'Scheduled' && <Calendar className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-8855" />}
                              <span className="text-[14px] text-[#475569]" data-id="element-8856">
                                {report.generatedBy}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-8857">
                            <span className="text-[14px] text-[#475569]" data-id="element-8858">
                              {report.fileSize}
                            </span>
                          </td>
                          <td className="px-4 py-3" data-id="element-8859">
                            <div className="flex items-center gap-2" data-id="element-8860">
                              {report.status === 'Completed' && <Badge variant="success" data-id="element-8861">
                                  <CheckCircle className="w-3 h-3 mr-1 inline" data-id="element-8862" />
                                  Completed
                                </Badge>}
                              {report.status === 'Failed' && <div className="flex items-center gap-2" data-id="element-8863">
                                  <Badge variant="error" data-id="element-8864">
                                    <XCircle className="w-3 h-3 mr-1 inline" data-id="element-8865" />
                                    Failed
                                  </Badge>
                                  {report.error && <span className="cursor-help" title={report.error} data-id="element-8866">
                                      <AlertCircle className="w-4 h-4 text-[#EF4444]" data-id="element-8867" />
                                    </span>}
                                </div>}
                              {report.status === 'Processing' && <Badge variant="warning" data-id="element-8868">
                                  <Loader2 className="w-3 h-3 mr-1 inline animate-spin" data-id="element-8869" />
                                  Processing
                                </Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-8870">
                            {report.status === 'Completed' && <Button variant="outline" size="sm" onClick={() => handleDownload(report.id)} iconLeft={<Download className="w-3.5 h-3.5" data-id="element-8872" />} data-id="element-8871">
                                Download
                              </Button>}
                            {report.status === 'Failed' && <button onClick={() => handleRetry(report.id)} className="text-[14px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-8873">
                                Retry
                              </button>}
                            {report.status === 'Processing' && <span className="text-[14px] text-[#94A3B8]" data-id="element-8874">
                                —
                              </span>}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Expiry Notice */}
              <div className="text-[12px] text-[#94A3B8] text-center" data-id="element-8875">
                Download links expire 30 days after generation
              </div>
            </> /* Empty State for Report History */ : <div className="bg-white rounded-lg border border-[#E2E8F0] p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8876">
              <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-8877">
                <FileText className="w-8 h-8 text-[#94A3B8]" data-id="element-8878" />
              </div>
              <h3 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-8879">
                No reports generated yet
              </h3>
              <p className="text-[14px] text-[#64748B] mb-6 max-w-md mx-auto" data-id="element-8880">
                Generate a report manually or set up a scheduled report
              </p>
              <Button variant="primary" onClick={() => setIsScheduleModalOpen(true)} data-id="element-8881">
                Generate Report
              </Button>
            </div>}
        </div>}

      {/* Schedule Reports Modal */}
      <ScheduleReportsModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} onSave={handleScheduleReport} data-id="element-8882" />
    </div>;
}